import type { Secret } from '@imprivata-cloud/dataprivacy';
import type { ConsentGrantType, ConsentType, Entitlement } from './constants';

export type Metadata = { rawMetadata?: string; metadataUrl?: string };

export type TenantInfo = { tenantId: string; tenantType: string };

export interface IdpMetadataGetResponse {
  idpMetadataInfoList: IdpMetadataInfo[];
  domainNames: string[];
}

export interface IdpMetadataInfo {
  idpMetadataId: string;
  idpId: string;
  idpDisplayName: string;
  metadataUrl?: string;
}

export interface IdpMetadataSaveRequest {
  metadataId?: string;
  displayName: string;
  domain: string;
  metadata?: Metadata;
}

export interface MetadataSaveResponse {
  entityId: string;
  metadataId: string;
}

// IdpConfigGet
export interface IdpConfigGetResponse {
  idpConfigId: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}
export interface IdpConfigGetRequest {
  idpMetadataId: string;
}

// IdpConfigsFind
export interface IdpConfigsFindRequest {
  idpMetadataId?: string;
}
export interface IdpConfigsFindResponse {
  idpConfigs: IdpConfig[];
}

export interface IdpConfig {
  idpConfigId: string;
  idpMetadataId: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}

// IdpConfigSave
export interface IdpConfigSaveRequest {
  idpMetadataId: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}
export interface IdpConfigSaveResponse {
  idpConfigId: string;
}

// IdpConfigUpdate
export interface IdpConfigUpdateRequest {
  idpMetadataId?: string;
  idpConfigId?: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}
export interface IdpConfigUpdateResponse {
  idpConfigId: string;
}

export interface OrgInfoGetResponse {
  logoUrl: string;
  orgName: string;
  items: string;
  businessEmail: string;
}

// All fields are optional and only the fields that are provided will be updated.
export interface OrgInfoSaveRequest {
  logoUrl?: string;
  orgName?: string;
  businessEmail?: string;
}

export interface OrgPreferencesGetResponse extends OrgPreferencesUpdateRequest {}

export interface OrgPreferencesUpdateRequest {
  logoUrl?: string;
  orgName?: string;
  usernameLabel?: string;
  passwordLabel?: string;
  proxCardLabel?: string;
}

export interface GetAadConsentRequest {
  aadTenantId: string;
}

export interface GetAadConsentResponse {
  aadConsentUrl: string;
}

export interface TenantSetupCompleteRequest {
  email: string;
  withEntraId: boolean;
  consent: ApiConsent;
}

export interface ApiConsent {
  grantType?: string; // setup rerun may not need DPA consent
  documentName: string;
}

export interface TenantLookupResponse {
  tenantIds: string[];
  tenantList: TenantInfo[];
}

export interface GetClientUserSessionResponse {
  tenantId?: string;
  tenantType?: string;
  userInfo?: UserInfo;
}

export type UserInfo = {
  username: string;
  email?: string;
  roleType: string;
  productExperienceVisitorId?: string;
  userUpn?: string;
  userDisplayName?: string;
  mfaRequiredDate?: string | null;
  mfaEnrolled?: boolean | null;
};

export interface EntraIdConsentUrlGetResponse {
  entraConsentUrl: string;
}

export interface EntraIdConsentUrlGetRequest {
  entraTenantId: string;
}

export interface EntraIdTenantSetupResponse {
  entraUserId: string;
}

export interface EntraIdAuthenticationTestRequest {
  providedName: string;
  password: Secret;
}

export interface UserIdentity {
  id: string;
  entraIdId: string;
  entraIdUpn?: string;
  adUpn?: string;
  samAccountName?: string;
  emailAddress?: string;
}

export type EntraIdAuthenticationTestResultType = 'success' | 'app' | 'system';

export interface EntraIdAuthenticationTestResults {
  code: string;
  type: EntraIdAuthenticationTestResultType;
  message: string;
  rawOutput?: Record<string, unknown>;
}

export interface EntraIdAuthenticationTestResponse {
  status: 'success' | 'failed';
  traceId: string;
  userIdentity?: UserIdentity;
  results: EntraIdAuthenticationTestResults;
}

export type OrgInfo = { name: string; logoUrl: string };

export type StartSsoLink = { startSsoUrl: string; applianceDesignator: string };

export type PurchasedProduct = {
  displayName: string;
  integrationId: string;
  productTag: string;
  spDisplayName: string;
  spMetadataId: string;
  startSsoLinks: StartSsoLink[];
};

export type NonPurchasedProduct = { displayName: string; productTag: string; learnMoreUrl: string };

export type ProductInfo = {
  purchasedProducts: PurchasedProduct[];
  nonPurchasedProducts: NonPurchasedProduct[];
};

export interface GetLandingPageResponse {
  orgInfo?: OrgInfo;
  productInfo: ProductInfo;
}

export enum FileAccessModifier {
  PRIVATE = 'private',
  PUBLIC = 'public',
}

export type PrepareFileUploadResponse = { uploadUrl: string; readUrl: string };

// DPA
export interface CheckDpaConsentRequest {
  consentDocumentName: string;
}
export interface CheckDpaConsentResponse {
  actionType: CheckDpaConsentResponseActionType;
  documentName: string; // example impr-dpa-consent-v2_0.html
  documentUrl: string; // example https://file-access.app.dev.imprivata.cloud/documents/consent/impr-dpa-consent-v2_0.html
}

export enum CheckDpaConsentResponseActionType {
  NONE = 'NONE', // Don't show the clickthrough
  CHECK_NEEDED = 'CHECK_NEEDED',
  UPDATE_NEEDED = 'UPDATE_NEEDED',
}

// create token
export interface ClientCreateRequest {
  enterpriseId: string;
  installationTokenExpirationInMinutes: number;
}

export interface ClientCreateResponse {
  clientMetadata: ClientMetadata;
}

export type ClientMetadata = {
  clientId: string;
  eamEnterpriseId: string;
  installationToken: TokenSecret;
  tokenExpiryTime: number;
  createdAt: number;
  installationTokenValue: string;
};
export type TokenSecret = { data: string; origin: null; isProxied: false };

export type GroupRequest = {
  groupName: string;
  groupId: string;
  userCount: number;
  syncEnabled: boolean;
  adminEnabled: boolean;
  idpDeleted: boolean;
};

export type FindDirectoriesResponse = { directories: Directory[] };

export type Directory = {
  name: string;
  directoryId: string;
  latestSyncAt: string;
  syncedGroupCount: number;
  idpTenantId: string;
};

export type ListSelectors = {
  fields?: string[];
  sortFields?: SortField[];
  filters?: Filter[];
  search?: SearchSelector;
};

export type SortField<T = string> = { field: T; order?: SortOrder };

export type Filter = {
  field: string;
  value?: string | boolean | number;
  operator?: FilterOperator;
};

export type SearchSelector<T = string> = { pattern: string; fields?: T[] };

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export enum FilterOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not-equals',
  NOT_NULL = 'not-null',
}

export const ALL_ITEMS_PATTERN = '*';

export type FindUsersRequest =
  | { selectors: ListSelectors; pageSize?: number }
  | { pageToken: string; pageSize?: number };

export type DirectorySyncProgressGetRequest = { directoryId: string };

export type DirectorySyncProgressGetResponse = {
  syncStatus: SyncStatus;
  failureReason?: SyncFailureReason;
  info?: SyncProgressInfo;
  latestSyncAt?: string;
};

export enum SyncStatus {
  NOT_INITIALIZED = 'not-initialized',
  RUNNING = 'running',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
}

export enum SyncFailureReason {
  IDP_FAILURE = 'idp-failure',
  SYNC_ALREADY_RUNNING = 'sync-already-running',
  SYNC_INTERRUPTED = 'sync-interrupted',
  SYSTEM_FAILURE = 'system-failure',
  TRANSIENT_IDP_FAILURE = 'transient-idp-failure',
  TRANSIENT_SYSTEM_FAILURE = 'transient-system-failure',
}

export type SyncProgressInfo = {
  directoryId: string;
  syncId: string;
  totalStepsProcessed: number;
  totalSteps: number;
};

export type DirectoryFailedSyncFindRequest = { directoryId: string };

export type DirectoriesFailedSyncFindResponse = { info: FailedSyncsFindInfo[] };

export type FailedSyncsFindInfo = {
  syncId: string;
  failureReason: string;
  createdAt: string;
  endedAt?: string;
};

export type FindUsersResponse = {
  users: User[];
  totalCount: number;
  latestUpdatedAt: string;
  pagingInfo?: PagingInfo;
};

export type User = {
  userId: string;
  displayName: string;
  upn: string;
  tenantId: string;
  idpTenantId: string;
  idpUserId: string;
  roleType: string;
  firstName?: string;
  lastName?: string;
  mobilePhone?: string;
  email?: string;
  lockedUntil?: string; // ISO 8601 date-time
  factorLockouts?: FactorLockout[];
  idpUserEnabled: boolean;
  createdAt: string; // ISO 8601 date-time
  updatedAt: string; // ISO 8601 date-time
};

export type PagingInfo = { afterToken: string; beforeToken: string };

export interface GroupForSyncMarkRequest {
  groupId: string;
  isAdmin: boolean;
}

export interface GroupForSyncUnmarkRequest {
  groupId: string;
}

export interface GroupsFindResponse {
  groups: Group[];
  totalCount: number;
  latestUpdatedAt: string;
  pagingInfo: PagingInfo;
}

export interface Group {
  groupName: string;
  groupId: string;
  userCount: number;
  syncEnabled: boolean;
  adminEnabled: boolean;
  idpDeleted: boolean;
}

export interface FindListRequest {
  selectors: {
    search?: { pattern: string; fields: string[] };
    filters: { field: string; value: string; operator: string }[];
  };
}

export interface DomainsValidateRequest {
  names: string[];
}

export interface AuthenticatorsFindByUserIdsRequest {
  userIds: string[];
}

export interface AuthenticatorsFindByUserIdsResponse {
  authenticators: Authenticator[];
}

export interface AuthenticatorsUnenrollRequest {
  authenticatorIds: string[];
}

export interface CredentialsFindRequest {
  userId: string;
}
export interface CredentialsFindResponse {
  credentialInfo: CredentialInfo[];
}

export interface CredentialInfo {
  credentialId: string;
  username: string;
  appId: string;
  appDisplayName: string;
}

export interface CredentialDeleteRequest {
  credentialId: string;
}

export enum FactorType {
  FACE = 'face',
  FIDO = 'fido',
  FINGERPRINT = 'fingerprint',
  PROX = 'prox',
  PASSWORD = 'pwd',
  PIN = 'impr-pin',
  USERNAME = 'username',
  TEMP_CODE = 'temp-code',
  IMPRIVATA_ID = 'impr-id',
  LEARNED_CREDS = 'learned-creds',
  OTP = 'email-otp',
}

export interface Authenticator {
  authenticatorId: string;
  userId: string;
  shortName: string;
  deviceModel?: string;
  lastUsedAt: string;
  factorType: FactorType;
  assuranceType: AssuranceType;
}

export enum AssuranceType {
  UNSPECIFIED = 'unspecified',
  IAL2_ASSURED = 'ial2-assured',
}

export interface ConsentInfo {
  actionType: CheckDpaConsentResponseActionType;
  consentType?: ConsentType; // @deprecated v1
  grantType: ConsentGrantType;
  documentMetadataList: Pick<
    ConsentDocumentMetadata,
    'documentName' | 'documentPurpose' | 'documentSource'
  >[]; // v2
  residenceState?: string; // @deprecated v1
  documentName?: string; // @deprecated v1
  documentUrl?: string; // @deprecated v1
}
export interface SelfServiceLandingPageDataResponse {
  orgInfo: { name: string; logoUrl: string };
  consentInfos: ConsentInfo[];
  authenticators: Authenticator[];
  credentials: CredentialInfo[];
  userInfo: { userId?: string; directoryId?: string; username?: string; externalId?: string };
  residenceLocations?: ResidenceLocation[];
  enabledFactors: string[];
}

export interface ConsentSubmitResponse {
  grantType: ConsentGrantType;
  documentName: string;
  residenceState?: string;
  consentType: ConsentType;
  userId?: string;
}

export interface ConsentSubmitRequest {
  consentType: ConsentType;
  grantType: ConsentGrantType;
  documentName: string;
  userId?: string;
  residenceState?: string;
}

export interface TenantInfoResponse {
  currentTenant: { entitlements: Entitlement[] };
  relatedTestTenants: { tenantId: string }[];
}

export interface SpDisplayNameUpdateRequest {
  spMetadataId: string;
  spDisplayName: string;
}

export interface SpDisplayNameUpdateResponse {
  spMetadataId: string;
}

export type ImprDomain = { domainName: string; isPrimary: boolean };

export type ImprDir = {
  directoryId?: string;
  name?: string;
  description?: string;
  type?: number;
  domains: ImprDomain[];
};

export interface ImprUserSaveRequest {
  user: ImprUser;
}

export interface ImprUserSaveResponse {
  userId: string;
}

export type ImprUserDeleteRequest = { userId: string };

export interface ImprDirResponse {
  directories: ImprDir[];
}

export type ImprUserRole = {
  roleId?: string;
  value: ImprUserRoleValue;
  displayName?: string;
  action?: Action;
  isPrimary?: boolean;
};

export enum ImprUserRoleValue {
  admin = 'admin',
  user = 'user',
}

export type GrpMembership = { groupId: string; displayName?: string };

export type FactorLockout = { factorType: string; lockedUntil: string };

export type ImprUser = {
  userId?: string;
  directoryId?: string;
  username: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  userType?: string;
  roles?: ImprUserRole[];
  userGroupMembership?: GrpMembership[];
  emails: ImprUserEmail[];
  phoneNumbers?: ImprPhoneNumber[];
  lockedUntil?: string;
  factorLockouts?: FactorLockout[];
};

export function findRole(role: ImprUserRoleValue, roles: ImprUserRole[] | undefined) {
  return roles?.find(r => r.value === role);
}

export function isAdmin(user: ImprUser | undefined) {
  return !!findRole(ImprUserRoleValue.admin, user?.roles);
}

export enum Action {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

export const addAdminRoleTemplate: ImprUserRole = {
  action: Action.ADD,
  value: ImprUserRoleValue.admin,
  displayName: '',
  isPrimary: true,
};

export function buildAddUserRole(isPrimary: boolean): ImprUserRole {
  return { action: Action.ADD, value: ImprUserRoleValue.user, displayName: '', isPrimary };
}

export type ImprUserEmail = {
  emailId?: string;
  value: string;
  isPrimary: boolean;
  action?: Action;
};

export type ImprPhoneNumber = {
  value: string;
  phoneNumberId?: string;
  type?: string;
  displayName?: string;
  isPrimary?: boolean;
  action?: Action;
};

export type PageInfo = { count: number; startIndex: number };

export interface ImprIdpUsersFindRequest {
  pageInfo?: PageInfo;
}

export interface ImprIdpUsersFindResponse {
  usersTotal: number;
  users: ImprUser[];
  pageInfo: PageInfo;
}

export interface IdpGetUserRequest {
  userId: string;
}

export interface IdpGetUserResponse {
  user: ImprUser;
}

export type InitialUser = { username: string; password: Secret; email?: string };

export interface DirectoryAddRequest {
  directory: ImprDir;
  initialUser?: InitialUser;
}

export interface InstallationToken {
  data: string;
}
export interface ClientCredential {
  clientCredentialId: string;
  clientMetadata?: ClientMetadata;
  installationToken: InstallationToken;
  installationTokenExpiry: number;
  createdAt: number;
  integrationId?: string;
  installationTokenValue: string;
}

export enum ClientCredentialClientType {
  PAS = 'pas',
  AIP = 'aip',
}

export interface CreateClientCredentialRequest {
  clientType: ClientCredentialClientType;
  clientMetadata?: ClientMetadata;
  installationTokenExpirationInMinutes: number;
  accessTokenExpirationInMinutes: number;
  integrationId?: string;
}

export interface CreateClientCredentialResponse {
  clientCredential: ClientCredential;
}

export type ImprDirectory = {
  name?: string;
  description?: string;
  directoryId?: string;
  type?: string;
  domains?: ImprDomain[];
};

export type ImprDirectoriesConfig = {
  cidrNotations: string[];
  imprDomainSuffix: string; // e.g. imprivata.id, dev.imprivata.id
};

export type ImprIdpDataGetResponse = {
  directories: ImprDirectory[];
  directoriesConfig: ImprDirectoriesConfig;
};

export type UserPasswordChangeRequest = { newPassword: Secret };

export type SettingWithConstraints = { value: number; max: number; min: number };

export enum DocumentPurpose {
  CONSENT = 'biometric-consent',
  NOTICE = 'biometric-notice',
}
export interface PrepareUploadRequest {
  filename: string;
  purpose: DocumentPurpose;
}

export interface PrepareUploadRespone {
  readUrl: string;
  uploadUrl: string;
  operationId: string;
}

export interface ResidenceLocationsSaveRequest {
  locations: string[];
}

export interface ResidenceLocationsSaveResponse {
  savedCount: number;
}
interface CustomerAgreementInfo {
  documentFilename: string;
  readUrl: string;
  documentPurpose: DocumentPurpose;
  documentSource: 'imprivata' | 'customer';
  requiredResidenceLocations: string[];
}
export interface ResidenceLocation {
  shortName: string;
  fullName: string;
  displayName: string;
}

export interface ConsentDocumentMetadata {
  documentName: string;
  readUrl: string;
  documentPurpose: 'biometric-consent' | 'biometric-notice';
  documentSource: 'customer' | 'imprivata';
  requiredResidenceLocations: ResidenceLocation[];
}

export interface UserConsentCheckRequest {
  userId?: string;
  userUpn?: string;
  consentType: string;
}

export interface UserConsentCheckResponse {
  actionType: string;
  grantType: string;
  documentMetadata: ConsentDocumentMetadata[];
}

export interface UserConsentSubmitRequest {
  consent: {
    consentType: ConsentType;
    userId: string;
    residenceLocationShortName: string;
    grantType: string;
    documentNameList: string[];
  };
  userUpn?: string;
  userDisplayName?: string;
}

export interface CustomerAgreementInfoGetResponse {
  customerAgreementInfoList: CustomerAgreementInfo[];
  residenceLocationList: ResidenceLocation[];
  customAgreementEnabled: boolean;
  customerMandatoryResidenceLocationList: ResidenceLocation[];
}

export interface LegalAgreementDeleteRequest {
  documentPurpose: DocumentPurpose;
}

type ConsentRevoke = Pick<
  ConsentDocumentMetadata,
  'documentName' | 'documentPurpose' | 'documentSource'
>;

export interface UserConsentRevokeRequest {
  consentRevokeList: ConsentRevoke[];
}

export type TempCodeAssignRequest = { userId: string; data?: Record<string, unknown> };

export type TemporaryCode = { data: string; origin?: string; isProxied?: boolean };

export type TempCodeAssignResponse = { tempCode: TemporaryCode };

export type TemporaryCodeMetadataGetRequest = { userId: string };

export type TemporaryCodeMetadataGetResponse = {
  userId: string;
  username: string;
  multiUse?: boolean;
  createdAt?: string;
  startsAt?: string;
  expiresAt?: string;
  authenticatorId: string;
};

export type SettingsGetRequest = { featureKey: string; categoryName: string; instanceKey?: string };

export type SettingsGetResponse = { settings: Setting[] };

export type SettingsUpdateRequest = { settings: Setting[] };

export type Setting = {
  settingId?: string;
  featureKey: string;
  configType: string;
  categoryName: string;
  instanceKey?: string;
  name: string;
  valueType: string;
  value: string;
};

export type UserUnlockRequest = { userId: string };

export type FactorLockoutClearRequest = { userId: string; factorTypes: string[] };

export type ImportFileSubmitRequest = { importFile: File };

export type ImportStatus = 'COMPLETED' | 'FAILED';

export type ImportResult = {
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  status: ImportStatus;
};

export type ImportFileSubmitResponse = { importResult: ImportResult; errorReport?: Blob };

export type UsersInviteRequest = { userIds: string[] };

export type UsersInviteResponse = {
  requestedInvitationsCount: number;
  scheduledInvitationsCount: number;
  rejectedInvitationsCount: number;
  rejectedInvitations?: RejectedInvitation[];
};

export type RejectedInvitation = { userId: string; reason: string };

export type ActivityReportGetRequest = { startDate?: string; endDate?: string };
export type LoginActivityReportGetRequest = ActivityReportGetRequest;
export type EpcsAuditReportGetRequest = ActivityReportGetRequest;

// Password Policy Configuration
// Matches OpenAPI spec: saas-api-idls/openapi/am-portal-config/v1/openapi/spec.yaml v1.2.1

export interface IntegerConstraints {
  min: number;
  max: number;
}

export interface IntegerSetting {
  value: number;
  constraints: IntegerConstraints;
}

export interface BooleanSetting {
  value: boolean;
}

export interface PasswordPolicyGetResponse {
  minimumLength: IntegerSetting;
  maximumLength: IntegerSetting;
  requireAlphabetic: BooleanSetting;
  requireNumeric: BooleanSetting;
  requireSpecial: BooleanSetting;
  requireLowercase: BooleanSetting;
  requireUppercase: BooleanSetting;
  passwordReuse: IntegerSetting;
  passwordExpiration: IntegerSetting;
}

export interface PasswordPolicyUpdateRequest {
  minimumLength: number;
  maximumLength: number;
  requireAlphabetic: boolean;
  requireNumeric: boolean;
  requireSpecial: boolean;
  requireLowercase: boolean;
  requireUppercase: boolean;
  passwordReuse: number;
  passwordExpiration?: number;
}

export interface AuthenticationStrengthGetRequest {
  applicationId: string;
}

export interface AuthenticationStrengthGetResponse {
  currentValue: string;
  availableValues: string[];
}

// Matches OpenAPI spec: am-portal-config/v1 — ProxCardMaxNumberGetResponse / ProxCardMaxNumberUpdateRequest
export interface ProxCardMaxNumberGetResponse {
  proxCardMaxNumber: number;
}

export interface ProxCardMaxNumberUpdateRequest {
  proxCardMaxNumber: number;
}

export type SupervisedAssuranceCancelRequest = { assuranceId: string };

export type SupervisedAssuranceStartRequest = { authenticatorId: string };

export type SupervisedAssuranceStartResponse = { assuranceId: string };

export type IdentityVerificationRecordRequest = {
  assuranceId: string;
  idDocuments: string[];
  comments?: string;
};

export type AuthenticatorVerifyRequest = {
  assuranceId: string;
  authenticatorId: string;
  factorType: string;
  factorData: Secret;
  systemData?: Secret;
};

export type SupervisedAssuranceCompleteRequest = {
  assuranceId: string;
  oidcAuthorizationCode: string;
  oidcCodeVerifier: string;
};

export type PasswordExpirationInfoGetRequest = Record<string, never>;

export type PasswordExpirationInfoGetResponse = { passwordExpiresAt?: string };

export type EmailVerificationRequest = Record<string, never>;
export type EmailVerificationResponse = { otpId: string };

export type VerificationOtpRequest = { otpId: string; otp: Secret };
export type VerificationOtpResponse = { verifiedAt: string };

// Applications / Epic Hyperdrive
export interface IdpCertificate {
  publicKey: string;
  idpCertificate: string;
}

export interface AppIntegration {
  appIntegrationId: string;
  appName: string;
  appDisplayName: string;
  idpIssuer: string | null;
  idpCertificates: IdpCertificate[];
  createdAt: string;
  updatedAt: string;
}

export interface AppIntegrationFindByNameRequest {
  appName: string;
}

export interface AppIntegrationFindByNameResponse {
  appIntegration: AppIntegration;
}

export interface EpicAppLockConfig {
  sharedWorkstation?: string;
  privateWorkstation?: string;
}

export enum WorkstationType {
  SHARED = 'shared',
  PRIVATE = 'private',
}

export interface AppConfig {
  samlEnabled: true;
  appUsernameAdMatch: boolean | null;
  interconnectBaseUrls: string[] | null;
  captureMaxRetries: number;
  epicAppLockConfig?: EpicAppLockConfig;
}

export interface AppConfigFindRequest {
  applicationId: string;
}

export interface AppConfigGetResponse {
  appConfig: AppConfig;
  redirectUri: string;
}

export interface AppConfigUpdateRequest {
  applicationId: string;
  appConfig: AppConfig;
}
